import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { process, State, orderBy } from '@progress/kendo-data-query';
import { CityService } from '../../services/city.service';
import { ContactService } from '../../services/contact.service';
import { UtilService } from '../../services/util.service';
import { Contact, Country } from '../../models/models';

@Component({
    selector: 'contact-grid',
    templateUrl: './contact-grid.component.html',
    standalone: false
})
export class ContactGrid implements OnChanges {
    @Input() tableID: number = 0;
    @Input() contactType: string = '';
    @Input() countryCode: string = 'US';

    public contactID: number = 0;
    public isAdd: boolean = false;
    public isEdit: boolean = false;

    private selectedTableID: number = 0;
    private selectedContactType: string = '';
    private usPhoneMask: string = "(999) 000-0000";
    private mexicoPhoneMask: string = "99-999-999-9999";

    public phoneFilter: string = 'US';
    public phoneMask: string = '(999) 000-0000';
    public countryList: Country[] = [];
    public contacts: Contact[] = [];
    public view: GridDataResult = { data: [], total: 0 };
    public state: State = { skip: 0, take: 20 };
    private editedRowIndex: number | undefined;
    private editedContact: Contact = {} as Contact;

    constructor(private cityService: CityService, private contactService: ContactService, private utilService: UtilService) {
        this.getCountries();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.tableID != null && changes.contactType != null) {
            var newTableID = parseInt(changes.tableID.currentValue.toString());
            var newContactType = changes.contactType.currentValue.toString();

            if (newTableID != this.selectedTableID || newContactType != this.selectedContactType) {
                this.selectedTableID = newTableID;
                this.selectedContactType = newContactType;

                this.getContacts();
            }
        }

        if (changes.countryCode != null && changes.countryCode.currentValue != null && changes.countryCode.currentValue.toString().length > 0) {
            this.phoneFilter = changes.countryCode.currentValue.toString();

            switch (changes.countryCode.currentValue.toString().toUpperCase()) {
                case 'US':
                    this.phoneMask = this.usPhoneMask;
                    break;
                case 'MX':
                    this.phoneMask = this.mexicoPhoneMask;
                    break;
                default:
                    this.phoneMask = this.usPhoneMask;
                    break;
            }
        }
    }

    private getCountries() {
        this.cityService.getCountries().subscribe(result => {
            this.countryList = result;
        });
    }

    private getContacts() {
        this.contactService.getContactsByTableIdType(this.selectedTableID, this.selectedContactType).subscribe(result => {
            this.contacts = result;
            this.contacts = this.contacts.filter(t => t.activeInd == true);

            this.contacts.forEach(c => {
                c.phone = c.phone == null ? '' : c.phone.replace('(', '').replace(')', '').replace(/-/g, '');
                c.cell = c.cell == null ? '' : c.cell.replace('(', '').replace(')', '').replace('-', '');
            });

            this.setContactsGrid();
        });
    }

    public getCountryAbbr(countryID: number) {
        var country = this.countryList.find(t => t.countryId == countryID);
        if (country == null) {
            return "US";
        }
        else {
            return country.abbrev.trim();
        }
    }

    private setContactsGrid() {
        if (this.state.sort) {
            this.contacts = orderBy(this.contacts, this.state.sort);
        }

        this.view = process(this.contacts, this.state);
    }

    public contactStateChange(state: DataStateChangeEvent) {
        this.state = state;
        this.setContactsGrid();
    }

    public refreshContact() {
        this.contactID = 0;
        this.isAdd = false;
        this.isEdit = false;
        this.getContacts();
    }

    public addNew() {
        this.contactID = 0;
        this.isEdit = false;
        this.isAdd = true;
    }

    public edit(contact: Contact) {
        this.contactID = contact.contactID;
        this.isAdd = false;
        this.isEdit = true;
    }

    public contactEditHandler({ sender, rowIndex, dataItem }) {
        this.contactCloseEditor(sender);

        this.editedRowIndex = rowIndex;
        this.editedContact = Object.assign({}, dataItem);

        sender.editRow(rowIndex);
    }

    public contactCancelHandler({ sender, rowIndex }) {
        this.contactCloseEditor(sender, rowIndex);
    }

    private contactCloseEditor(grid, rowIndex = this.editedRowIndex) {
        grid.closeRow(rowIndex);
        this.resetContact(this.editedContact);
        this.editedRowIndex = undefined;
        this.editedContact = {} as Contact;
    }

    private resetContact(contact: Contact) {
        if (!contact) return;

        const originalContact = this.contacts.find(c => c.contactID == contact.contactID)!;
        Object.assign(originalContact, contact);
    }

    public contactSaveHandler({ sender, rowIndex, dataItem, isNew }) {
        this.utilService.isLoading = true;

        this.contactService.saveContact(dataItem as Contact).subscribe(result => {
            this.utilService.isLoading = false;

            if (result.contactID > 0) {
                this.getContacts();
            } else {
                alert('Failed to save Contact');
            }
        });

        sender.closeRow(rowIndex);

        this.editedRowIndex = undefined;
        this.editedContact = {} as Contact;
    }

    public contactAddHandler({ sender }) {
        this.contactCloseEditor(sender);

        var newContact = {
            contactType: this.selectedContactType,
            tableID: this.selectedTableID,
            defaultContactInd: this.contacts.length < 1,
            firstName: '',
            lastName: '',
            title: '',
            phone: '',
            phoneExt: '',
            fax: '',
            cell: '',
            email: '',
            erpContactID: 0,
            createDate: new Date(),
            createEmployeeID: parseInt(localStorage.getItem('EmployeeId')!),
            emailBlastIncluded: this.selectedContactType.toLowerCase() == 'customer',
            driverInd: false,
            excludeFromOfferEmailInd: false,
            enteredOfferInd: false
        } as Contact;

        sender.addRow(newContact);
    }

    public removeHandler({ sender, rowIndex, dataItem, isNew }) {
        this.utilService.isLoading = true;
        this.contactCloseEditor(sender);

        this.contactService.deleteContactById(dataItem.contactID).subscribe(result => {
            this.utilService.isLoading = false;
            this.getContacts();
        });
    }
}
